import { buttonFields } from 'molecules/Button/config';
import { iconIds } from 'molecules/Icon/types';

import type { Input } from '@builder.io/sdk';

const headingIconEnum = iconIds.map(id => ({ label: id, value: id }));

export const moleculeHeadingFields = [
  {
    name: 'isQuizResultsHeading',
    type: 'boolean',
    defaultValue: false,
    helperText:
      'Check this ONLY for the heading that should display Build vs Buy Quiz results. Leave unchecked for all other headings on the page.',
  },
  {
    name: 'as',
    type: 'string',
    enum: [
      { label: 'H1', value: 'h1' },
      { label: 'H2', value: 'h2' },
      { label: 'H3', value: 'h3' },
      { label: 'H4', value: 'h4' },
      { label: 'H5', value: 'h5' },
      { label: 'H6', value: 'h6' },
    ],
    helperText: 'Controls the semantic heading level (H1-H6) for SEO and accessibility.',
  },
  {
    name: 'eyebrow',
    type: 'string',
    helperText: 'Optional short text displayed above the main heading (e.g. a category or label).',
  },
  {
    name: 'eyebrowWords',
    type: 'list',
    helperText:
      'Colored eyebrow: each item is a word/phrase with its own color. Overrides the plain eyebrow field when set.',
    subFields: [
      { name: 'text', type: 'string', required: true, helperText: 'Text for this word or phrase.' },
      { name: 'color', type: 'color', helperText: 'Optional color override. Defaults to teal (#296462).' },
    ],
  },
  {
    name: 'heading',
    type: 'string',
    defaultValue: 'Heading Text',
    helperText:
      'Main heading text. Supports line breaks with <br /> tags. Use {icon} to embed the icon picked below at that position.',
  },
  {
    name: 'headingIcon',
    type: 'string',
    enum: headingIconEnum,
    helperText:
      'Optional. Pick an icon from the sprite to embed inline in the heading. Place the {icon} placeholder inside the heading text where you want it to appear.',
  },
  {
    name: 'subheading',
    type: 'string',
    helperText: 'Optional subheading displayed below the main heading. Supports line breaks with <br /> tags.',
  },
  {
    name: 'body',
    type: 'richText',
    defaultValue: '<p>Here is your Copy. Click on me to edit content!</p>',
    helperText: 'Body copy that appears below the heading. Supports formatted rich text.',
  },
  {
    name: 'buttons',
    type: 'list',
    subFields: [{ name: 'id', type: 'string', required: true, showIf: 'false' }, ...buttonFields],
    helperText: 'Add up to 2 buttons below the heading. Each button can have text, link, and styles.',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onChange: (options: Map<string, any>) => {
      if (options.get('buttons').length > 0) {
        options.get('buttons').forEach((item: Map<string, string>, index: number) => {
          item.set('id', `button-${index}`);
        });
      }
      if (options.get('buttons').length > 2) {
        options.set('buttons', options.get('buttons').slice(0, 2));
        alert('Maximum button count is 2. Removing excess buttons.');
      }
    },
  },
  {
    name: 'secondaryCta',
    type: 'object',
    helperText: 'Optional text link below the buttons (e.g., "Or, take a self-guided product tour →")',
    subFields: [
      {
        name: 'text',
        type: 'string',
        helperText: 'Link text (e.g., "Or, take a self-guided product tour")',
      },
      {
        name: 'url',
        type: 'string',
        helperText: 'Link destination URL',
      },
      {
        name: 'openInNewTab',
        type: 'boolean',
        defaultValue: false,
        helperText: 'Open link in new tab',
      },
    ],
  },
  {
    name: 'alignment',
    type: 'string',
    defaultValue: 'center',
    enum: [
      { label: 'Start', value: 'left' },
      { label: 'Center', value: 'center' },
      { label: 'CenterWide', value: 'centerWide' },
      { label: 'Inline', value: 'leftInline' },
    ],
    helperText: 'Controls text alignment of the heading and body within the section.',
  },
  {
    name: 'sidePadding',
    type: 'string',
    enum: [
      { label: 'Default', value: 'default' },
      { label: 'None', value: 'none' },
      { label: 'Small', value: 'sm' },
      { label: 'Medium', value: 'md' },
      { label: 'Large', value: 'lg' },
      { label: 'XL', value: 'xl' },
    ],
    helperText: 'Sets horizontal padding inside the container for spacing on left/right.',
  },
] as const satisfies DeepReadonly<Input[]>;

export type MoleculeHeadingConfigProps = GenerateBuilder<typeof moleculeHeadingFields>;
